import { type } from 'arktype';
import { createPaginatedResponseSchema } from '../shared/pagination';

/** Contrato del catálogo `location` (país/región/localidad o spot puntual). */
export const locationResponseSchema = type({
	country: 'string',
	createdAt: 'string.date.iso',
	id: 'string.uuid',
	isSpot: 'boolean',
	lat: 'number | null',
	lng: 'number | null',
	region: 'string | null',
	town: 'string | null',
}).onUndeclaredKey('delete');

export type LocationResponse = typeof locationResponseSchema.infer;

/**
 * CL-3 (regla adicional autorizada): sin exigir `region` o `town`, se podrían crear
 * localizaciones vacías a las que luego apunta una experiencia. Se exige al menos uno.
 */
export const createLocationSchema = type({
	// Por defecto `'ES'` cuando el cliente no la envía.
	country: "string = 'ES'",
	// Por defecto `false` (localización de área, no un punto exacto).
	isSpot: 'boolean = false',
	'lat?': 'number',
	'lng?': 'number',
	'region?': 'string',
	'town?': 'string',
})
	.onUndeclaredKey('delete')
	.narrow((data, ctx) => {
		if (data.region === undefined && data.town === undefined) {
			return ctx.reject({ message: 'debe indicarse "region" o "town"', path: [] });
		}
		return true;
	});

export type CreateLocation = typeof createLocationSchema.infer;

// No se deriva con `.partial()` de `createLocationSchema`: esta tiene `country`/`isSpot`
// con valor por defecto, y `.partial()` seguiría rellenando ese valor por defecto en una
// actualización que no toca esos campos — lo que reescribiría datos existentes sin que el
// cliente lo pidiera. Se declara la forma parcial a mano, sin defaults.
export const updateLocationSchema = type({
	'country?': 'string',
	'isSpot?': 'boolean',
	'lat?': 'number',
	'lng?': 'number',
	'region?': 'string',
	'town?': 'string',
}).onUndeclaredKey('delete');

export type UpdateLocation = typeof updateLocationSchema.infer;

export const locationPaginatedResponseSchema = createPaginatedResponseSchema(locationResponseSchema);

export type LocationPaginatedResponse = typeof locationPaginatedResponseSchema.infer;
